import { useState, useEffect } from "react";
import { 
    AiOutlineDownload, AiOutlineVideoCamera, AiOutlineSearch, 
    AiOutlineEdit, AiOutlinePlus, AiOutlineArrowRight, AiOutlineArrowLeft, AiOutlineDelete, AiOutlineUpload
} from "react-icons/ai";
import { MDBCardTitle, MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import Dropdown from "../add-camera-dropdown/index";
import { 
    addCameraService, 
    deleteCameraService, 
    getCameraListService, 
    scanNetworkService, 
    testRtspService ,
    uploadVideoService
} from "../../services/api-service";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

const CameraSetting = () => {
    // Modes: 'select' (Initial choice), 'add' (Wizard flow), 'edit' (Edit existing)
    const [mode, setMode] = useState("select");
    
    // Flow Steps for Add Mode
    const [step, setStep] = useState(1);
    const [cameraData, setCameraData] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState({});

    // Network Scan States
    const [subnet, setSubnet] = useState("");
    const [scannedIps, setScannedIps] = useState([]);
    const [isScanning, setIsScanning] = useState(false);

    // Form States
    const [brand, setBrand] = useState("hikvision_ip");
    const [inputType, setInputType] = useState("auto"); 
    const [ip, setIp] = useState("");
    const [username, setUsername] = useState("admin");
    const [password, setPassword] = useState("");
    const [channel, setChannel] = useState("1"); // Default channel
    
    const [rtsp, setRtsp] = useState("");
    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    
    const [liveImage, setLiveImage] = useState(null);
    const [isLoadingLive, setIsLoadingLive] = useState(false);

    // Video Upload States
    const [videoFile, setVideoFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        handleGetCameraList();
    }, []);

    // Populate fields when an existing camera is selected
    useEffect(() => {
        if (selectedCamera && selectedCamera.camera_number) {
            setName(selectedCamera.name || "");
            setRtsp(selectedCamera.rtsp || "");
            setLocation(selectedCamera.location || "");
            setLiveImage(null); // Clear any previous live image
        }
    }, [selectedCamera]);

    // Auto-generate RTSP when form values change
    useEffect(() => {
        if (inputType === "auto" && ip && mode === "add") {
            let generatedRtsp = "";
            let ch = channel || "1";
            
            switch (brand) {
                // IP Cameras
                case "hikvision_ip":
                case "generic_ip":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554`;
                    break;
                case "dahua_ip":
                case "cp_plus_ip":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/cam/realmonitor?channel=1&subtype=0`;
                    break;
                case "hanwha_ip":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/profile1/media.smp`;
                    break;
                
                // NVR / DVR Systems
                case "hikvision_nvr":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/Streaming/Channels/${ch}`;
                    break;
                case "dahua_nvr":
                case "cpplus_nvr":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/cam/realmonitor?channel=${ch}&subtype=0`;
                    break;
                case "uniview_nvr":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/realmonitor?channel=${ch}`;
                    break;
                case "tvt_nvr":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554/chID=${ch}&streamType=main`;
                    break;
                case "generic_nvr":
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554`;
                    break;
                default:
                    generatedRtsp = `rtsp://${username}:${password}@${ip}:554`;
            }
            
            setRtsp(generatedRtsp);
        }
    }, [brand, ip, username, password, channel, inputType, mode]);

    const handleGetCameraList = () => {
        getCameraListService()
            .then((response) => setCameraData(response?.data))
            .catch(() => toast.error("Failed to get camera list"));
    };

    const handleScanNetwork = async () => {
        setIsScanning(true);
        setScannedIps([]);
        try {
            const response = await scanNetworkService(subnet);
            const foundIps = response?.data?.discovered_ips || [];
            const scannedSubnet = response?.data?.scanned_subnet;
            
            if (!subnet && scannedSubnet) setSubnet(scannedSubnet);
            setScannedIps(foundIps);
            
            if (foundIps.length > 0) toast.success(`Found ${foundIps.length} active camera(s)!`);
            else toast.warning(`No cameras found on subnet ${scannedSubnet}x`);
        } catch (error) {
            toast.error("Network scan failed.");
        }
        setIsScanning(false);
    };

    const handleCheckLive = async () => {
        setIsLoadingLive(true);
        try {
            const response = await testRtspService({ rtsp_url: rtsp });
            setLiveImage(response?.data?.image);
            toast.success("Live stream fetched successfully!");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Failed to fetch live feed. Check credentials.");
        }
        setIsLoadingLive(false);
    };

    const handleSaveCamera = async () => {
        let camera_number = 1;
        if (mode === "edit" && selectedCamera?.camera_number) {
            camera_number = selectedCamera.camera_number;
        } else if (cameraData?.length > 0) {
            camera_number = Math.max(...cameraData.map(c => c.camera_number)) + 1;
        }

        const body = { name, location, rtsp, assign: [], camera_number };

        addCameraService(body).then(() => {
            toast.success(mode === "edit" ? 'Camera updated successfully' : 'Camera saved successfully');
            handleGetCameraList();
            resetFlow();
        }).catch((error) => {
            toast.error(error?.response?.data?.detail || "Failed to save camera");
        });
    };

    const handleDeleteCamera = async () => {
        if (!selectedCamera.camera_number) return;
        deleteCameraService({ camera_number: selectedCamera.camera_number }).then(() => {
            toast.success('Camera deleted successfully');
            setCameraData(cameraData.filter(item => item.camera_number !== selectedCamera.camera_number));
            resetFlow();
        }).catch((error) => {
            toast.error(error?.response?.data?.detail || "Failed to delete camera");
        });
    };

const handleVideoUpload = async () => {
        if (!videoFile) return toast.warning("Please select a video file first!");
        
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", videoFile);

        try {
            // Using your dedicated API service which already handles the auth tokens!
            await uploadVideoService(formData);
            
            toast.success("Video uploaded and added to the database successfully!");
            setVideoFile(null);
            document.getElementById('videoUploadInput').value = ''; 
            
            // Refresh the camera list so the new video appears in the dropdown
            handleGetCameraList(); 
            
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Upload failed. Please check permissions.");
        }
        setIsUploading(false);
    };
    const resetFlow = () => {
        setMode("select");
        setStep(1);
        setRtsp(""); setName(""); setLocation(""); setIp(""); setPassword("");
        setLiveImage(null); setScannedIps([]); setSelectedCamera({});
        setChannel("1");
        setVideoFile(null);
    };

    // Helper component for Step Indicators
    const Stepper = ({ currentStep }) => (
        <div className="d-flex justify-content-between mb-5 position-relative mx-auto" style={{ maxWidth: '400px' }}>
            <div className="progress position-absolute top-50 start-0 w-100 translate-middle-y" style={{ height: '4px', zIndex: 0 }}>
                <div className="progress-bar bg-primary" role="progressbar" style={{ width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%', transition: 'width 0.4s ease' }}></div>
            </div>
            {[1, 2, 3].map(s => (
                <div key={s} className={`d-flex align-items-center justify-content-center rounded-circle position-relative z-1 shadow-sm ${currentStep >= s ? 'bg-primary text-white' : 'bg-white text-secondary border'}`} style={{ width: '45px', height: '45px', fontWeight: 'bold', fontSize: '1.1rem', transition: 'all 0.3s ease' }}>
                    {s}
                </div>
            ))}
        </div>
    );

    return (
        <div className="container-fluid py-5" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="row d-flex justify-content-center">
                <div className="col-12 col-md-10 col-lg-8 col-xl-10">
                    <MDBCard className="shadow-sm border-0 rounded-4">
                        <MDBCardBody className="p-4 p-md-5">
                            
                            {/* =========================================
                                MODE: INITIAL SELECTION
                            ========================================= */}
                            {mode === "select" && (
                                <div className="text-center py-4">
                                    <h2 className="fw-bold mb-2 text-dark">Camera Management</h2>
                                    <p className="text-muted mb-5">Select an action below to get started</p>
                                    
                                    <div className="row g-4 justify-content-center">
                                        <div className="col-12 col-md-4">
                                            <div 
                                                className="card h-100 border-0 shadow-sm text-center p-4" 
                                                style={{ cursor: 'pointer', backgroundColor: '#eef2ff', transition: 'transform 0.2s' }}
                                                onClick={() => setMode("add")}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                            >
                                                <div className="mb-3 text-primary"><AiOutlinePlus size={48} /></div>
                                                <h5 className="fw-bold text-primary">Add New Camera</h5>
                                                <p className="text-muted small mb-0">Scan network or enter RTSP directly to configure a new device.</p>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <div 
                                                className="card h-100 border-0 shadow-sm text-center p-4" 
                                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', transition: 'transform 0.2s' }}
                                                onClick={() => setMode("edit")}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                            >
                                                <div className="mb-3 text-secondary"><AiOutlineEdit size={48} /></div>
                                                <h5 className="fw-bold text-dark">Edit Existing Camera</h5>
                                                <p className="text-muted small mb-0">Modify settings or remove an already configured camera.</p>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <div 
                                                className="card h-100 border-0 shadow-sm text-center p-4" 
                                                style={{ backgroundColor: '#fff3e0', transition: 'transform 0.2s' }}
                                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                            >
                                                <div className="mb-3 text-warning"><AiOutlineUpload size={48} /></div>
                                                <h5 className="fw-bold text-warning">Upload Video</h5>
                                                <p className="text-muted small mb-3">Upload Your CCTV footage in mp4 to server</p>
                                                
                                                <div className="d-flex flex-column gap-2 mt-auto">
                                                    <input 
                                                        type="file" 
                                                        id="videoUploadInput"
                                                        accept="video/*" 
                                                        className="form-control form-control-sm" 
                                                        onChange={(e) => setVideoFile(e.target.files[0])}
                                                    />
                                                    <button 
                                                        className="btn btn-warning w-100 shadow-sm text-white fw-bold" 
                                                        onClick={handleVideoUpload}
                                                        disabled={isUploading || !videoFile}
                                                    >
                                                        {isUploading ? (
                                                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Uploading...</>
                                                        ) : (
                                                            "Upload File"
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* =========================================
                                MODE: EDIT EXISTING CAMERA
                            ========================================= */}
                            {mode === "edit" && (
                                <div className="animate__animated animate__fadeIn">
                                    <div className="d-flex justify-content-between align-items-center mb-4 pb-2">
                                        <div>
                                            <h4 className="fw-bold mb-1 text-dark">Edit Configuration</h4>
                                            <p className="text-muted small mb-0">Select and modify an existing camera</p>
                                        </div>
                                        <button className="btn btn-light btn-sm text-muted rounded-pill px-4 shadow-sm border" onClick={resetFlow}>
                                            <AiOutlineArrowLeft className="me-2 d-none d-md-inline" /> Back to Menu
                                        </button>
                                    </div>
                                    
                                    <div className="p-4 bg-white border border-2 rounded-4 shadow-sm mb-4">
                                        <label className="form-label text-primary text-uppercase small fw-bold mb-2 d-flex align-items-center gap-2">
                                            <AiOutlineSearch size={18} /> Choose Camera
                                        </label>
                                        <div className="w-100">
                                            <Dropdown addCamera={false} cameraData={cameraData} selectedCamera={selectedCamera} setSelectedCamera={setSelectedCamera} />
                                        </div>
                                    </div>

                                    {!selectedCamera?.camera_number ? (
                                        <div className="text-center py-5 mt-4 text-muted border border-2 rounded-4 bg-light" style={{ borderStyle: 'dashed !important' }}>
                                            <div className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm mb-3" style={{ width: '80px', height: '80px' }}>
                                                <AiOutlineVideoCamera size={36} className="text-secondary" />
                                            </div>
                                            <h5 className="fw-bold text-dark">No Camera Selected</h5>
                                            <p className="small text-muted mb-0">Please select a camera from the dropdown above to modify its settings.</p>
                                        </div>
                                    ) : (
                                        <div className="bg-white border border-2 rounded-4 shadow-sm p-4 animate__animated animate__fadeInUp">
                                            <h5 className="mb-4 fw-bold text-dark d-flex align-items-center gap-2 border-bottom pb-3">
                                                <AiOutlineEdit className="text-primary"/> Camera Properties
                                            </h5>
                                            
                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">Location / Room Number</label>
                                                    <input type="text" className="form-control form-control-lg rounded-3 fs-6 bg-light border-0 shadow-none" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. 1102" />
                                                </div>
                                                
                                                <div className="col-md-6">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">Camera Name</label>
                                                    <input type="text" className="form-control form-control-lg rounded-3 fs-6 bg-light border-0 shadow-none" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front Gate" />
                                                </div>
                                                
                                                <div className="col-12">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">RTSP Stream URL</label>
                                                    <div className="input-group input-group-lg shadow-sm rounded-3 overflow-hidden border">
                                                        <input type="text" className="form-control fs-6 bg-white border-0 shadow-none" value={rtsp} onChange={(e) => setRtsp(e.target.value)} />
                                                        <button className="btn btn-primary d-flex align-items-center gap-2 px-4 shadow-none" onClick={handleCheckLive} disabled={isLoadingLive || !rtsp} style={{ zIndex: 0 }}>
                                                            {isLoadingLive ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <AiOutlineVideoCamera size={20} />}
                                                            Test Stream
                                                        </button>
                                                    </div>
                                                </div>

                                                {liveImage && (
                                                    <div className="col-12 mt-3 animate__animated animate__fadeIn">
                                                        <div className="bg-dark rounded-4 shadow-sm overflow-hidden d-flex align-items-center justify-content-center position-relative border border-3 border-dark" style={{ height: "350px" }}>
                                                            <img src={liveImage} alt="Live Stream" style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                                                            <div className="position-absolute top-0 start-0 m-3 px-3 py-1 bg-success text-white rounded-pill small fw-bold shadow-sm d-flex align-items-center">
                                                                <span className="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true" style={{ width: '10px', height: '10px' }}></span>
                                                                LIVE
                                                            </div>
                                                            <button className="btn btn-light btn-sm position-absolute top-0 end-0 m-3 rounded-circle shadow" onClick={() => setLiveImage(null)} style={{ width: '32px', height: '32px', padding: 0 }}>✕</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mt-5 pt-4 border-top">
                                                <button className="btn btn-outline-danger btn-lg rounded-pill px-4 d-flex align-items-center justify-content-center gap-2 transition-all" onClick={handleDeleteCamera}>
                                                    <AiOutlineDelete size={20} /> Delete Camera
                                                </button>
                                                <button className="btn btn-success btn-lg rounded-pill px-5 d-flex align-items-center justify-content-center gap-2 shadow" onClick={handleSaveCamera}>
                                                    <AiOutlineDownload size={20} /> Save Changes
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* =========================================
                                MODE: ADD NEW CAMERA (WIZARD STEPS)
                            ========================================= */}
                            {mode === "add" && (
                                <div className="animate__animated animate__fadeIn">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h4 className="fw-bold mb-0">Add New Camera</h4>
                                        <button className="btn btn-light btn-sm text-muted rounded-pill px-3 shadow-sm border" onClick={resetFlow}>Cancel Setup</button>
                                    </div>
                                    
                                    <Stepper currentStep={step} />

                                    {/* STEP 1: RTSP Setup */}
                                    {step === 1 && (
                                        <div>
                                            <h5 className="mb-4 fw-semibold text-center text-dark">Setup Connection</h5>
                                            
                                            <div className="row justify-content-center mb-4">
                                                <div className="col-md-8">
                                                    <div className="btn-group w-100 shadow-sm rounded-3" role="group">
                                                        <input type="radio" className="btn-check" name="inputType" id="auto" autoComplete="off" checked={inputType === 'auto'} onChange={() => setInputType('auto')} />
                                                        <label className={`btn ${inputType === 'auto' ? 'btn-primary' : 'btn-light border'} py-2`} htmlFor="auto">Network Scan</label>
                                                        
                                                        <input type="radio" className="btn-check" name="inputType" id="direct" autoComplete="off" checked={inputType === 'direct'} onChange={() => setInputType('direct')} />
                                                        <label className={`btn ${inputType === 'direct' ? 'btn-primary' : 'btn-light border'} py-2`} htmlFor="direct">Manual Entry</label>
                                                    </div>
                                                </div>
                                            </div>

                                            {inputType === "auto" && (
                                                <div className="bg-light p-4 rounded-4 border mb-4">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">Network Scanner (Port 554)</label>
                                                    <div className="d-flex gap-2 mb-4">
                                                        <input type="text" className="form-control form-control-lg rounded-3 fs-6" value={subnet} onChange={(e) => setSubnet(e.target.value)} placeholder="Leave blank to auto-detect (e.g. 10.0.0.)" />
                                                        <button className="btn btn-primary rounded-3 px-4 d-flex align-items-center gap-2" onClick={handleScanNetwork} disabled={isScanning} style={{ whiteSpace: 'nowrap' }}>
                                                            <AiOutlineSearch size={20} /> {isScanning ? "Scanning..." : "Scan"}
                                                        </button>
                                                    </div>

                                                    {scannedIps.length > 0 && (
                                                        <div className="mb-4">
                                                            <label className="form-label text-success text-uppercase small fw-bold">Select Discovered IP</label>
                                                            <select className="form-select form-select-lg border-success rounded-3 fs-6" onChange={(e) => setIp(e.target.value)}>
                                                                <option value="">-- Choose an IP --</option>
                                                                {scannedIps.map(foundIp => <option key={foundIp} value={foundIp}>{foundIp}</option>)}
                                                            </select>
                                                        </div>
                                                    )}

                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <label className="form-label text-muted text-uppercase small fw-bold">Camera Brand / Type</label>
                                                            <select className="form-select rounded-3" value={brand} onChange={(e) => setBrand(e.target.value)}>
                                                                <optgroup label="IP Cameras">
                                                                    <option value="hikvision_ip">Hikvision IP</option>
                                                                    <option value="dahua_ip">Dahua IP</option>
                                                                    <option value="cp_plus_ip">CP Plus IP</option>
                                                                    <option value="hanwha_ip">Hanwha (Hanva) IP</option>
                                                                    <option value="generic_ip">Generic IP</option>
                                                                </optgroup>
                                                                <optgroup label="NVR / DVR">
                                                                    <option value="hikvision_nvr">Hikvision NVR / DVR</option>
                                                                    <option value="dahua_nvr">Dahua NVR / DVR</option>
                                                                    <option value="cpplus_nvr">CP Plus NVR / DVR</option>
                                                                    <option value="uniview_nvr">Uniview NVR / DVR</option>
                                                                    <option value="tvt_nvr">TVT NVR</option>
                                                                    <option value="generic_nvr">Generic DVR</option>
                                                                </optgroup>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label text-muted text-uppercase small fw-bold">IP Address</label>
                                                            <input type="text" className="form-control rounded-3" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="192.168.1.100" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label text-muted text-uppercase small fw-bold">Username</label>
                                                            <input type="text" className="form-control rounded-3" value={username} onChange={(e) => setUsername(e.target.value)} />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label text-muted text-uppercase small fw-bold">Password</label>
                                                            <input type="password" className="form-control rounded-3" value={password} onChange={(e) => setPassword(e.target.value)} />
                                                        </div>
                                                        
                                                        {/* Conditionally show Channel only if an NVR/DVR is selected */}
                                                        {brand.includes("nvr") && (
                                                            <div className="col-md-6">
                                                                <label className="form-label text-muted text-uppercase small fw-bold">Channel Number</label>
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control rounded-3" 
                                                                    value={channel} 
                                                                    onChange={(e) => setChannel(e.target.value)} 
                                                                    placeholder={brand === "hikvision_nvr" ? "101, 201..." : "1, 2..."} 
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mb-4">
                                                <label className="form-label text-muted text-uppercase small fw-bold">RTSP URL</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-lg rounded-3 fs-6"
                                                    value={rtsp} 
                                                    onChange={(e) => setRtsp(e.target.value)} 
                                                    placeholder="rtsp://admin:password@ip:554"
                                                />
                                                <div className="form-text mt-2 small text-muted">
                                                    You can manually edit this URL before testing the connection.
                                                </div>
                                            </div>

                                            <div className="text-end border-top pt-4">
                                                <button className="btn btn-primary btn-lg rounded-pill px-5 d-inline-flex align-items-center gap-2 shadow-sm" disabled={!rtsp} onClick={() => { setStep(2); handleCheckLive(); }}>
                                                    Next Step <AiOutlineArrowRight />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 2: Live Preview */}
                                    {step === 2 && (
                                        <div className="text-center">
                                            <h5 className="mb-4 fw-semibold text-dark">Live Camera Verification</h5>
                                            
                                            <div className="mb-4 bg-dark rounded-4 shadow-sm overflow-hidden d-flex align-items-center justify-content-center border border-3 border-dark" style={{ height: "350px", width: "100%", position: 'relative' }}>
                                                {liveImage ? (
                                                    <img src={liveImage} alt="Live Stream" style={{ height: "100%", width: "100%", objectFit: "contain" }} />
                                                ) : (
                                                    <div className="text-muted d-flex flex-column align-items-center">
                                                        <AiOutlineVideoCamera size={48} className="mb-3 opacity-50" />
                                                        <span>{isLoadingLive ? "Connecting to camera..." : "No Preview Available"}</span>
                                                    </div>
                                                )}
                                                
                                                {isLoadingLive && (
                                                    <div className="position-absolute w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center text-white" style={{ top: 0, left: 0 }}>
                                                        <div className="spinner-border text-primary" role="status"></div>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="d-flex justify-content-between align-items-center mt-5 border-top pt-4">
                                                <button className="btn btn-light btn-lg rounded-pill px-4 d-flex align-items-center gap-2 border" onClick={() => setStep(1)}>
                                                    <AiOutlineArrowLeft /> Back
                                                </button>
                                                <div className="d-flex gap-3">
                                                    <button className="btn btn-outline-primary btn-lg rounded-pill px-4" onClick={handleCheckLive} disabled={isLoadingLive}>
                                                        Retry Connection
                                                    </button>
                                                    <button className="btn btn-primary btn-lg rounded-pill px-5 d-flex align-items-center gap-2 shadow-sm" onClick={() => setStep(3)} disabled={!liveImage}>
                                                        Approve <AiOutlineArrowRight />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* STEP 3: Details & Save */}
                                    {step === 3 && (
                                        <div>
                                            <h5 className="mb-4 fw-semibold text-center text-dark">Final Details</h5>
                                            
                                            <div className="row g-4 mb-5">
                                                <div className="col-12">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">Location / Room Number</label>
                                                    <input type="text" className="form-control form-control-lg rounded-3 fs-6 bg-light border-0 shadow-none" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Server Room 1" />
                                                </div>
                                                
                                                <div className="col-12">
                                                    <label className="form-label text-muted text-uppercase small fw-bold">Camera Name</label>
                                                    <input type="text" className="form-control form-control-lg rounded-3 fs-6 bg-light border-0 shadow-none" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Entrance" />
                                                </div>
                                            </div>

                                            <div className="d-flex justify-content-between border-top pt-4">
                                                <button className="btn btn-light btn-lg rounded-pill px-4 d-flex align-items-center gap-2 border" onClick={() => setStep(2)}>
                                                    <AiOutlineArrowLeft /> Back
                                                </button>
                                                <button className="btn btn-success btn-lg rounded-pill px-5 d-flex align-items-center gap-2 shadow-sm" onClick={handleSaveCamera}>
                                                    <AiOutlineDownload size={20} /> Save & Finish
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </MDBCardBody>
                    </MDBCard>
                </div>
            </div>
        </div>
    );
};

export default CameraSetting;
